import { AuthContext, useIsHydrated } from '@core/shared';
import { EmailTemplateType, MarketingCampaignStatus, OptionsApi } from '@core/swagger-client';
import { Button, Confirm, ContainerDefault, Edit, ErrorMessage, ErrorMessagePage, Heading, Input, LoaderPage, TabGroup, WarningOutlined } from '@core/ui';
import { useNavigate, useParams } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useContext, useEffect, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { AudienceTab } from './AudienceTab';
import { CampaignRecipientsTable } from './CampaignRecipientsTable';
import { EmailTemplateTab } from './EmailTemplateTab';

const formatDate = (value?: string | null) =>
    value ? DateTime.fromISO(value).toLocal().setLocale(getI18n().language).toLocaleString(DateTime.DATETIME_SHORT) : '-';

export const WebinarCampaignPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    const nav = useNavigate();
    const isHydrated = useIsHydrated();
    const queryClient = useQueryClient();

    const { webinarId, id } = useParams({ strict: false });
    const wid = webinarId ?? '';
    const campaignId = id ?? '';

    const detailQuery = OptionsApi.marketingCampaigns.getDetail(wid, campaignId, { enabled: !!wid && !!campaignId });
    const { data: campaign, isLoading, error } = useQuery(detailQuery);
    const listsQuery = OptionsApi.marketingCampaigns.getLists(wid, { enabled: !!wid });
    const { data: lists } = useQuery(listsQuery);
    const {
        data: webinarSummary,
        isLoading: isSummaryLoading,
        error: summaryError
    } = useQuery(OptionsApi.dashboard.getContentSummary(wid, { enabled: !!wid }));
    const isPublished = webinarSummary?.isPublished ?? false;
    // Only fetch the default template for brand-new campaigns (no subject/body yet).
    const { data: defaultTemplate } = useQuery(
        OptionsApi.emailLists.getDefaultTemplate(EmailTemplateType.WebinarEmailList, {
            enabled: !!campaign && !campaign.subject && !campaign.body
        })
    );

    const [activeTab, setActiveTab] = useState(0);
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [excludeInterested, setExcludeInterested] = useState(false);
    const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
    const [seeded, setSeeded] = useState(false);

    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState('');

    const [sendOpen, setSendOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const updateMutation = useMutation(OptionsApi.marketingCampaigns.update());
    const sendMutation = useMutation(OptionsApi.marketingCampaigns.send());
    const removeMutation = useMutation(OptionsApi.marketingCampaigns.remove());
    const emailListRemoveMutation = useMutation(OptionsApi.emailLists.remove());

    // Seed local state from the loaded campaign (once).
    useEffect(() => {
        if (!campaign || seeded) return;
        setName(campaign.name);
        setSubject(campaign.subject);
        setBody(campaign.body);
        setExcludeInterested(campaign.excludeInterested);
        setSelectedListIds(campaign.targetListIds);
        setSeeded(true);
    }, [campaign, seeded]);

    // Pre-fill brand-new drafts from the default template (runs after seeding).
    useEffect(() => {
        if (!seeded || !defaultTemplate) return;
        if (!subject && !body) {
            setSubject(defaultTemplate.subject);
            setBody(defaultTemplate.body);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seeded, defaultTemplate]);

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }
    if (error || summaryError) {
        return <ErrorMessagePage apiError={error ?? summaryError ?? undefined} />;
    }
    // Wait for the content summary too: until it loads, isPublished is false, which would flash the
    // "not published" warning and block Send for a webinar that is actually published.
    if (isLoading || isSummaryLoading || !campaign || !seeded) {
        return <LoaderPage />;
    }

    const isDraft = campaign.status === MarketingCampaignStatus.Draft;
    const allLists = [...(lists?.webinar ?? []), ...(lists?.global ?? [])];
    const estimatedRecipients = isDraft ? allLists.filter((l) => selectedListIds.includes(l.id)).reduce((sum, l) => sum + l.recipientCount, 0) : null;
    const invalidate = () => queryClient.invalidateQueries({ queryKey: detailQuery.queryKey });
    const invalidateLists = () => queryClient.invalidateQueries({ queryKey: listsQuery.queryKey });
    // Partial key match — invalidates all pages/filters for this campaign's recipients.
    const invalidateRecipients = () => queryClient.invalidateQueries({ queryKey: ['marketingCampaigns', 'getRecipients', wid, campaignId] });

    const startEditName = () => {
        setNameDraft(name);
        setEditingName(true);
    };

    const saveName = () =>
        updateMutation.mutate(
            { webinarId: wid, id: campaign.id, name: nameDraft.trim(), subject, body, excludeInterested, targetListIds: selectedListIds },
            {
                onSuccess: () => {
                    setName(nameDraft.trim());
                    setEditingName(false);
                    invalidate();
                }
            }
        );

    const save = (onSuccess?: () => void, overrideTargetListIds?: string[]) =>
        updateMutation.mutate(
            { webinarId: wid, id: campaign.id, name, subject, body, excludeInterested, targetListIds: overrideTargetListIds ?? selectedListIds },
            {
                onSuccess: () => {
                    invalidate();
                    invalidateRecipients();
                    onSuccess?.();
                }
            }
        );

    const addGlobalLists = (newIds: string[]) => {
        const updated = [...new Set([...selectedListIds, ...newIds])];
        setSelectedListIds(updated);
        save(undefined, updated);
    };

    const handleSend = () =>
        save(() =>
            sendMutation.mutate(
                { webinarId: wid, id: campaign.id },
                {
                    onSuccess: () => {
                        setSendOpen(false);
                        setSeeded(false);
                        invalidate();
                    }
                }
            )
        );

    const handleDelete = () =>
        removeMutation.mutate({ webinarId: wid, id: campaign.id }, { onSuccess: () => nav({ to: '/$webinarId/marketing', params: { webinarId: wid } }) });

    const toggleList = (id: string) => setSelectedListIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

    const removeList = (id: string) => setSelectedListIds((ids) => ids.filter((x) => x !== id));

    const deleteWebinarList = (id: string) => {
        const newListIds = selectedListIds.filter((x) => x !== id);
        setSelectedListIds(newListIds);
        emailListRemoveMutation.mutate(id, {
            onSuccess: () =>
                updateMutation.mutate(
                    { webinarId: wid, id: campaign.id, name, subject, body, excludeInterested, targetListIds: newListIds },
                    {
                        onSuccess: () => {
                            invalidateLists();
                            invalidate();
                            invalidateRecipients();
                        }
                    }
                )
        });
    };

    const mutationError = updateMutation.error ?? sendMutation.error ?? removeMutation.error ?? emailListRemoveMutation.error;
    const isSendFormIncomplete = !subject.trim() || !body.trim();

    return (
        <ContainerDefault>
            <BackLink to="/$webinarId/marketing" params={{ webinarId: wid }}>
                {t('Button.Back')}
            </BackLink>
            {editingName ? (
                <div className="flex flex-col gap-2 mb-6 max-w-lg">
                    <Input
                        data-testid="campaign-name-input"
                        label={t('Campaign.Field.Name')}
                        value={nameDraft}
                        onChange={(event) => setNameDraft(event.target.value)}
                    />
                    <div className="flex gap-2">
                        <Button
                            data-testid="campaign-name-save"
                            variant="success"
                            isLoading={updateMutation.isPending}
                            disabled={!nameDraft.trim()}
                            onClick={saveName}
                        >
                            {t('Generic.Button.Save')}
                        </Button>
                        <Button variant="warning-outline" onClick={() => setEditingName(false)}>
                            {t('Generic.Button.Cancel')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-3 mb-4 lg:mb-6 2xl:mb-8">
                    <Heading variant="h1">{name}</Heading>
                    {isDraft && (
                        <Button data-testid="campaign-name-edit" iconbtn onClick={startEditName}>
                            <Edit classNames="size-6 stroke-primary" />
                            <span className="sr-only">{t('Campaign.Field.Name.Edit')}</span>
                        </Button>
                    )}
                </div>
            )}

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-lg mb-8">
                <dt className="font-medium">{t('Campaign.Detail.SentAt')}</dt>
                <dd>{campaign.sentAt ? formatDate(campaign.sentAt) : t('Campaign.Detail.NotSent')}</dd>
                <dt className="font-medium">{t(isDraft ? 'Campaign.Detail.EstimatedRecipients' : 'Campaign.Detail.RecipientCount')}</dt>
                <dd>{isDraft ? estimatedRecipients : campaign.recipientCount}</dd>
                {!isDraft && (
                    <>
                        <dt className="font-medium">{t('Campaign.Detail.Sent')}</dt>
                        <dd>{campaign.sentCount}</dd>
                        <dt className="font-medium">{t('Campaign.Detail.Failed')}</dt>
                        <dd>{campaign.failedCount}</dd>
                        <dt className="font-medium">{t('Campaign.Detail.Blacklisted')}</dt>
                        <dd>{campaign.blacklistedCount}</dd>
                        <dt className="font-medium">{t('Campaign.Detail.AlreadyInterested')}</dt>
                        <dd>{campaign.alreadyInterestedCount}</dd>
                        <dt className="font-medium">{t('Campaign.Detail.AlreadySentInAnotherList')}</dt>
                        <dd>{campaign.alreadySentInAnotherListCount}</dd>
                        <dt className="font-medium">{t('Campaign.Detail.InterestedNow')}</dt>
                        <dd>{campaign.interestedNowCount}</dd>
                    </>
                )}
            </dl>

            {mutationError && <ErrorMessage apiError={mutationError} classNames="mb-4 max-w-4xl" />}

            <TabGroup direction={TabGroup.direction.HORIZONTAL}>
                <TabGroup.TabList numTabs={3} activeTab={activeTab} setActiveTab={setActiveTab} classNames="border-b border-primary mb-6">
                    <TabGroup.Tab
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        index={0}
                        className="nav-tab-border"
                        activeClassName="nav-tab--active"
                        data-testid="campaign-tab-audience"
                    >
                        {t('Campaign.Tab.Audience')}
                    </TabGroup.Tab>
                    <TabGroup.Tab
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        index={1}
                        className="nav-tab-border"
                        activeClassName="nav-tab--active"
                        data-testid="campaign-tab-addresses"
                    >
                        {t('Campaign.Tab.Addresses')}
                    </TabGroup.Tab>
                    <TabGroup.Tab
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        index={2}
                        className="nav-tab-border"
                        activeClassName="nav-tab--active"
                        data-testid="campaign-tab-email-template"
                    >
                        {t('Campaign.Tab.EmailTemplate')}
                    </TabGroup.Tab>
                </TabGroup.TabList>

                <TabGroup.TabPanel activeTab={activeTab} setActiveTab={setActiveTab} index={0} className="" activeClassName="block" inactiveClassName="hidden">
                    <AudienceTab
                        webinarId={wid}
                        campaignId={campaign.id}
                        campaignStatus={campaign.status}
                        isPublished={isPublished}
                        lists={lists}
                        excludeInterested={excludeInterested}
                        onExcludeInterestedChange={setExcludeInterested}
                        selectedListIds={selectedListIds}
                        onToggle={toggleList}
                        onRemove={removeList}
                        onDeleteWebinarList={deleteWebinarList}
                        onAddGlobalLists={addGlobalLists}
                        onSave={() => save()}
                        isSaving={updateMutation.isPending}
                        onChanged={() => {
                            setSeeded(false);
                            invalidate();
                        }}
                    />
                </TabGroup.TabPanel>

                <TabGroup.TabPanel activeTab={activeTab} setActiveTab={setActiveTab} index={1} className="" activeClassName="block" inactiveClassName="hidden">
                    <CampaignRecipientsTable webinarId={wid} campaignId={campaign.id} lists={lists} />
                </TabGroup.TabPanel>

                <TabGroup.TabPanel activeTab={activeTab} setActiveTab={setActiveTab} index={2} className="" activeClassName="block" inactiveClassName="hidden">
                    <EmailTemplateTab
                        subject={subject}
                        body={body}
                        readOnly={!isDraft}
                        onSubjectChange={setSubject}
                        onBodyChange={setBody}
                        onSave={() => save()}
                        isSaving={updateMutation.isPending}
                    />
                </TabGroup.TabPanel>
            </TabGroup>

            {isDraft && (
                <div className="mt-10">
                    {(isSendFormIncomplete || selectedListIds.length === 0 || !isPublished) && (
                        <div id="campaign-send-warning" className="bg-warning-light rounded-md inline-flex flex-col mb-4 ps-3 pe-4 py-2">
                            <span className="inline-flex items-center gap-2">
                                <WarningOutlined classNames="size-6 fill-warning shrink-0" />
                                {t('Campaign.CannotSend.Header')}
                            </span>
                            <ul className="list-disc list-inside ps-8 mt-1">
                                {!subject.trim() && <li>{t('Campaign.CannotSend.IncompleteSubject')}</li>}
                                {!body.trim() && <li>{t('Campaign.CannotSend.IncompleteBody')}</li>}
                                {selectedListIds.length === 0 && <li>{t('Campaign.CannotSend.NoLists')}</li>}
                                {!isPublished && <li>{t('Campaign.CannotSend.NotPublished')}</li>}
                            </ul>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-4">
                        <Button
                            data-testid="campaign-send"
                            variant="primary"
                            isLoading={sendMutation.isPending || updateMutation.isPending}
                            disabled={!isHydrated}
                            aria-disabled={isSendFormIncomplete || selectedListIds.length === 0 || !isPublished}
                            aria-describedby={
                                (isSendFormIncomplete || selectedListIds.length === 0 || !isPublished)
                                    ? 'campaign-send-warning'
                                    : undefined
                            }
                            onClick={() => {
                                if (!isPublished) return;
                                setSendOpen(true);
                            }}
                        >
                            {t('Campaign.Send')}
                        </Button>
                        <Button data-testid="campaign-delete" variant="warning" disabled={!isHydrated} onClick={() => setDeleteOpen(true)}>
                            {t('Campaign.Delete')}
                        </Button>
                    </div>
                </div>
            )}

            <Confirm
                open={sendOpen}
                onClose={() => setSendOpen(false)}
                onConfirm={handleSend}
                title={t('Campaign.SendConfirm.Title')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('Campaign.Send')}
                confirmTestId="campaign-send-confirm"
            >
                <p>{t('Campaign.SendConfirm.Body')}</p>
            </Confirm>

            <Confirm
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                title={t('Campaign.DeleteConfirm.Title')}
                closeText={t('Generic.Button.Cancel')}
                confirmText={t('Campaign.Delete')}
                confirmTestId="campaign-delete-confirm"
            >
                <p>{t('Campaign.DeleteConfirm.Body')}</p>
            </Confirm>
        </ContainerDefault>
    );
};
